import { faChevronUp, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	storefrontProductPath,
	type ItemPromoHeadline,
	type PortalCatalogVariantGroupDto,
	type PortalCatalogVariantOptionDto,
} from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { useCartStore } from '../../stores/cartStore';
import { AddToCartControl } from '../AddToCartControl';
import { FavoriteButton } from '../FavoriteButton';
import { ProductMetaLines } from '../ProductMetaLines';

/**
 * PD-16509 — a storefront search result can stand for a product whose
 * variants show as one result. Customers here re-order the same items often,
 * so every variant must be addable straight from the results: the grid card's
 * add button opens these options in a sheet, and the list view shows them as
 * rows under the product. Prices render through the catalog's own price
 * renderer so a variant reads exactly as its own card would.
 */
export type RenderCatalogPrice = (
	price: string | null,
	contractPrice: string | null,
	promoHeadline?: ItemPromoHeadline | null,
) => JSX.Element;

/** The price a customer pays before promos: the contract price when there is one. */
const basePrice = (option: PortalCatalogVariantOptionDto): number => {
	const contract =
		option.item.contractPrice != null
			? parseFloat(option.item.contractPrice)
			: NaN;
	if (Number.isFinite(contract)) {
		return contract;
	}
	const list = option.item.price != null ? parseFloat(option.item.price) : NaN;
	return Number.isFinite(list) ? list : NaN;
};

/**
 * The group's price line: the one price when every variant costs the same,
 * otherwise "From" the cheapest. With no price on any variant it is the
 * first variant's own line ("Call for price", or nothing when pricing is
 * hidden).
 */
export function VariantGroupPrice({
	group,
	renderPrice,
}: {
	group: PortalCatalogVariantGroupDto;
	renderPrice: RenderCatalogPrice;
}): JSX.Element {
	const { t } = useTranslation();
	const priced = group.options.filter((option) =>
		Number.isFinite(basePrice(option)),
	);
	if (priced.length === 0) {
		const first = group.options[0].item;
		return renderPrice(first.price, first.contractPrice, first.promoHeadline);
	}
	const cheapest = priced.reduce((low, option) =>
		basePrice(option) < basePrice(low) ? option : low,
	);
	const varies = priced.some(
		(option) => basePrice(option) !== basePrice(cheapest),
	);
	return (
		<span className="inline-flex items-baseline gap-1">
			{varies && <span className="text-xs text-brand-muted">{t('From')}</span>}
			{renderPrice(
				cheapest.item.price,
				cheapest.item.contractPrice,
				cheapest.item.promoHeadline,
			)}
		</span>
	);
}

/**
 * One variant: image, name, item number, price and stock, and its add control.
 * The picture and the name are links to the variant's product page (PD-17174),
 * so they open in a new tab and search engines can follow them.
 */
export function VariantOptionRow({
	option,
	renderPrice,
	showFavorite,
}: {
	option: PortalCatalogVariantOptionDto;
	renderPrice: RenderCatalogPrice;
	showFavorite: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const display = useDisplaySettings();
	const { item } = option;

	return (
		<div
			className="flex items-center gap-3 py-2.5"
			data-testid={`variant-option-${item.id}`}
		>
			{/* PD-17144: the thumbnail and its box go when the brand hides
			    item images; the name button beside it still opens the item. */}
			{display.showItemImages && (
				<Link
					to={storefrontProductPath(item.id)}
					className="w-12 h-12 shrink-0 bg-brand-input rounded-md overflow-hidden"
					aria-label={t(option.label)}
				>
					<ResilientImage
						src={item.primaryImageUrl}
						alt={item.description ?? ''}
						className="w-full h-full object-cover"
						loading="lazy"
						fallback={<ImagePlaceholder size="sm" />}
					/>
				</Link>
			)}
			<Link
				to={storefrontProductPath(item.id)}
				className="flex-1 min-w-0 text-left flex flex-col gap-0.5"
			>
				<span className="text-sm font-medium text-default line-clamp-2 leading-snug">
					{t(option.label)}
				</span>
				{item.externalId != null && (
					<span className="text-xs text-subtle truncate">
						{t('#')}
						{t(item.externalId)}
					</span>
				)}
				<span className="text-sm font-semibold text-default">
					{renderPrice(item.price, item.contractPrice, item.promoHeadline)}
				</span>
				<ProductMetaLines
					price={item.price}
					unitOfMeasure={item.unitOfMeasure}
					quantityOnHand={item.quantityOnHand}
					quantityOnHandDisplay={item.quantityOnHandDisplay}
					quantityShownMinimum={item.quantityShownMinimum}
					expirationDate={item.expirationDate}
					display={display}
				/>
			</Link>
			<div className="shrink-0 flex items-center gap-2">
				{showFavorite && (
					<FavoriteButton itemId={item.id} isFavorite={item.isFavorite === true} />
				)}
				{display.canPurchase && (
					<AddToCartControl
						itemId={item.id}
						variant="floating"
						inStock={item.inStock}
						deaSchedule={item.designations.deaSchedule}
						isPriced={item.isPriced}
					/>
				)}
			</div>
		</div>
	);
}

/**
 * The add button for a grouped product. It opens the options (the grid's
 * sheet, or the list row's variant rows) instead of adding one variant the
 * customer did not choose, and shows how many of the product's variants are
 * already in the cart. In the list it also collapses the rows again.
 */
export function VariantOptionsButton({
	group,
	onOpen,
	expanded,
}: {
	group: PortalCatalogVariantGroupDto;
	onOpen: () => void;
	expanded?: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const cart = useCartStore((state) => state.cart);
	const inCart = useMemo(() => {
		const ids = new Set(group.options.map((option) => option.item.id));
		return (cart?.lineItems ?? [])
			.filter((line) => line.fkItemId != null && ids.has(line.fkItemId))
			.reduce((sum, line) => sum + line.quantityOrdered, 0);
	}, [cart, group]);

	return (
		<button
			type="button"
			onClick={(event) => {
				event.stopPropagation();
				onOpen();
			}}
			aria-label={expanded === true ? t('Hide options') : t('Choose an option')}
			aria-expanded={expanded}
			data-testid="variant-options-button"
			className="relative w-9 h-9 rounded-full bg-portal-accent text-white hover:bg-portal-accent-hover flex items-center justify-center shadow-md transition-colors"
		>
			<FontAwesomeIcon
				icon={expanded === true ? faChevronUp : faPlus}
				className="text-sm"
			/>
			{inCart > 0 && (
				<span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-brand-surface text-portal-accent border border-portal-accent text-[10px] font-semibold flex items-center justify-center tabular-nums">
					{t(String(inCart))}
				</span>
			)}
		</button>
	);
}

/**
 * The options sheet: a bottom sheet on a phone and a centered dialog on
 * larger screens, the same shell as the storefront's other dialogs.
 */
export function VariantOptionsSheet({
	group,
	onClose,
	renderPrice,
	showFavorite,
}: {
	group: PortalCatalogVariantGroupDto;
	onClose: () => void;
	renderPrice: RenderCatalogPrice;
	showFavorite: boolean;
}): JSX.Element {
	const { t } = useTranslation();

	useEffect(() => {
		const onKey = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				onClose();
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [onClose]);

	return (
		<>
			<button
				type="button"
				aria-label={t('Close')}
				onClick={onClose}
				className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
			/>
			<div
				className="fixed inset-x-0 bottom-[max(calc(36px_+_env(safe-area-inset-bottom)),48px)] z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden filter-tray-in max-h-[80vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:rounded-2xl md:border md:border-brand-border"
				role="dialog"
				aria-modal="true"
				aria-label={t('Choose an option')}
				data-testid="variant-options-sheet"
			>
				<div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-brand-border shrink-0">
					<div className="min-w-0">
						<h2 className="text-base font-semibold text-default line-clamp-2">
							{t(group.productDescription ?? group.options[0].label)}
						</h2>
						<p className="text-xs text-brand-muted">
							{`${t(String(group.options.length))} ${t('options')}`}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label={t('Close')}
						className="p-2 -mr-2 text-brand-muted hover:text-default min-h-touch"
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>
				<div className="overflow-y-auto px-4 pb-2 divide-y divide-brand-border">
					{group.options.map((option) => (
						<VariantOptionRow
							key={option.item.id}
							option={option}
							renderPrice={renderPrice}
							showFavorite={showFavorite}
						/>
					))}
				</div>
			</div>
		</>
	);
}
